/** @jsx jsx */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { Box } from '@material-ui/core';

import { ICustomTheme } from 'interfaces';
import { Title, Text } from 'components';

const Container = styled(Box)(({ theme }: { theme: ICustomTheme }) => ({
  marginBottom: theme.spacing(18),
  marginLeft: `min(${theme.spacing(12)}px, 10%)`,
  marginRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    margin: 0,
    textAlign: 'center',
  },
}));

const StatusTitle = styled(Title)(({ theme }: { theme: ICustomTheme }) => ({
  display: 'block',
  marginBottom: theme.spacing(2.5),
  textAlign: 'inherit',
  textTransform: 'none',
  color: theme.colors.paletteFromBO.main,
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.h3.fontSize,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.h5.fontSize,
    marginBottom: theme.spacing(0.5),
  },
}));

const SubTitle = styled(Text)(
  ({
    isAFileUploaded,
    theme,
  }: {
    isAFileUploaded: boolean;
    theme: ICustomTheme;
  }) => ({
    color: theme.colors.grey[isAFileUploaded ? 'dark' : 'light'],
    fontSize: theme.typography[isAFileUploaded ? 'h3' : 'body1'].fontSize,
    textAlign: 'inherit',
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.h5.fontSize,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.caption.fontSize,
    },
  }),
);

export default function Status({
  isAFileUploaded,
  fileName,
  error,
}: {
  isAFileUploaded: boolean;
  fileName?: string;
  error?: string;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <Container>
      {!error ? (
        <React.Fragment>
          <StatusTitle color="primary" size="h2">
            {t(
              `challenges.declaration.proof.${
                isAFileUploaded ? 'statusSuccess' : 'statusTitle'
              }`,
            )}
          </StatusTitle>
          <SubTitle isAFileUploaded={isAFileUploaded}>
            {isAFileUploaded
              ? fileName
              : t('challenges.declaration.proof.ocrStatusSubtitle')}
            {' '}
            {!isAFileUploaded &&
              t('challenges.declaration.summary.fileMustBeReadable')}
          </SubTitle>
        </React.Fragment>
      ) : (
        <StatusTitle color="error" size="h4" css={{ fontWeight: 'normal' }}>
          {error}
        </StatusTitle>
      )}
    </Container>
  );
}
